import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { StationViewType } from '@/types';

import useBreakpoint from '@/hooks/use-break-point';
import { useGetStationData } from '@/hooks/use-get-data';
import {
  INCIDENT_DETAILS,
  INCIDENTS_LIST,
  LABELS,
  LIST,
  MONITOR,
  STATION_DETAILS,
} from '@/utils/constants';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENT_SEARCH,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_MONITOR,
  SEARCH_PARAM_MONITOR_PASSENGER,
  SEARCH_PARAM_MONITOR_RHB,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATIONS,
  SEARCH_PARAM_VEREINA_STATUS,
  SEARCH_PARAM_VIEW,
  VIEW_LADDER,
} from '@/utils/constants/queries';

const useStationsState = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [hasStationDetailsError, setHasStationDetailsError] = useState(false);
  const [isStationDetailsLoading, setIsStationDetailsLoading] = useState(true);

  const isPanelOpen = searchParams.get(SEARCH_PARAM_SHOW) === SEARCH_PARAM_STATIONS;
  const isMobile = useBreakpoint('mobile');

  const paramStationId = searchParams.get(SEARCH_PARAM_STATION_ID);
  const paramLockId = searchParams.get(SEARCH_PARAM_LOCK_PIN) ?? '';
  const paramIncidentId = searchParams.get(SEARCH_PARAM_INCIDENT_ID);
  const isIncidentsView = searchParams.get(SEARCH_PARAM_INCIDENTS) === 'true';
  const isMonitorView = !!searchParams.get(SEARCH_PARAM_MONITOR);

  const selectedMonitor = searchParams.get(SEARCH_PARAM_MONITOR) ?? '';
  const lockType = searchParams?.get(SEARCH_PARAM_LOCK_PIN_TYPE);
  const isLadderView = searchParams.get(SEARCH_PARAM_VIEW) === VIEW_LADDER;
  const paramSectionId = searchParams.get(SEARCH_PARAM_SECTION_ID);

  const isLockPinned = !!paramLockId && !!paramStationId;
  const isStationSelected = searchParams.get(SEARCH_PARAM_LOCK_PIN_TYPE) === 'station';
  const currentStationId = paramStationId || paramLockId;

  const currentTab = searchParams.get(SEARCH_PARAM_SHOW);
  const isStationsTabActive = currentTab === SEARCH_PARAM_STATIONS;

  const getViewType = (): StationViewType => {
    const isStationLocked = lockType === 'station' && (!currentTab || isStationsTabActive);

    const hasUsablePinnedStation = Boolean(paramLockId) && !isPanelOpen && isStationLocked;

    const shouldShowStationDetails = Boolean(paramStationId) || hasUsablePinnedStation;

    if (paramIncidentId) {
      return INCIDENT_DETAILS;
    }

    if (isIncidentsView) {
      return INCIDENTS_LIST;
    }

    if (isMonitorView) {
      return MONITOR;
    }

    if (shouldShowStationDetails) {
      return STATION_DETAILS;
    }

    return LIST;
  };

  const viewType = getViewType();

  const { data: station } = useGetStationData({
    id: currentStationId,
    enabled: !!currentStationId,
  });
  const { passengerMonitorLink, rhbMonitorLink, designationOfficial } = station ?? {};

  const monitorTitle = useMemo(() => {
    const map = {
      [SEARCH_PARAM_MONITOR_PASSENGER]: t(LABELS.passengerMonitor),
      [SEARCH_PARAM_MONITOR_RHB]: t(LABELS.rhbMonitor),
    };

    return map[selectedMonitor as keyof typeof map] || null;
  }, [selectedMonitor]);

  const headerText = useMemo(() => {
    if (viewType === MONITOR) {
      return monitorTitle;
    }

    if (viewType === INCIDENT_DETAILS) {
      return t(LABELS.incidentsDetails);
    }

    if (viewType === INCIDENTS_LIST) {
      return `${t(LABELS.incidents)} (${designationOfficial})`;
    }

    if (viewType === STATION_DETAILS) {
      return t(LABELS.stationDetails);
    }

    return t(LABELS.stations);
  }, [viewType, monitorTitle, designationOfficial, t]);

  const handleClose = useCallback(() => {
    const updatedParams = new URLSearchParams();

    if (isLadderView) {
      updatedParams.set(SEARCH_PARAM_VIEW, VIEW_LADDER);

      if (paramSectionId) {
        updatedParams.set(SEARCH_PARAM_SECTION_ID, paramSectionId);
      }
    }

    if (paramLockId) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN, paramLockId);
      updatedParams.set(SEARCH_PARAM_DRAWER, 'true');
    }

    if (lockType) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN_TYPE, lockType);
    }
    setSearchParams(updatedParams);
  }, [isLadderView, paramSectionId, paramLockId, lockType]);

  const handleBack = useCallback(() => {
    setSearchParams((prev) => {
      const currentIncidentSearch = prev.get(SEARCH_PARAM_INCIDENT_SEARCH);

      if (prev.get(SEARCH_PARAM_INCIDENT_ID)) {
        prev.delete(SEARCH_PARAM_INCIDENT_ID);

        return prev;
      }

      if (prev.get(SEARCH_PARAM_INCIDENTS)) {
        prev.delete(SEARCH_PARAM_INCIDENTS);

        if (currentIncidentSearch) {
          prev.set(SEARCH_PARAM_INCIDENT_SEARCH, currentIncidentSearch);
        } else {
          prev.delete(SEARCH_PARAM_INCIDENT_SEARCH);
        }

        return prev;
      }

      if (isMonitorView) {
        prev.delete(SEARCH_PARAM_MONITOR);

        return prev;
      }

      prev.delete(SEARCH_PARAM_STATION_ID);
      prev.delete(SEARCH_PARAM_VEREINA_STATUS);

      if (isLockPinned && isStationSelected) {
        prev.set(SEARCH_PARAM_LOCK_PIN, paramLockId);
        prev.set(SEARCH_PARAM_LOCK_PIN_TYPE, 'station');
      }

      return prev;
    });
  }, [isLockPinned, isStationSelected, paramLockId, isMonitorView, viewType]);

  const viewsWithBackIcon = [STATION_DETAILS, INCIDENT_DETAILS, INCIDENTS_LIST, MONITOR];
  const viewsWithHeaderBorder = [STATION_DETAILS, INCIDENT_DETAILS];

  return {
    viewType,
    isShowSidePanel: isPanelOpen,
    hasBackIcon: viewsWithBackIcon.includes(viewType),
    hasHeaderBottomBorder: viewsWithHeaderBorder.includes(viewType),
    isFullscreen: viewType === MONITOR && !isMobile,
    headerText: headerText || 'Stations',
    passengerMonitorLink,
    rhbMonitorLink,
    hasStationDetailsError,
    setHasStationDetailsError,
    isStationDetailsLoading,
    setIsStationDetailsLoading,
    handleClose,
    handleBack,
    currentStationId,
  };
};

export default useStationsState;
